import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  IconButton, Chip, Tooltip, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, FormControl,
  InputLabel, Select, MenuItem, Divider, LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Search, Plus, History, RefreshCw, Upload, Receipt, X, Clock, CloudDownload, FileCheck2,
} from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";
import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

// "classify" is the turn classifier. Rows here are optional: without them the model's own llm
// rows price it, and with them the classifier is costed separately from conversation.
const CAPABILITIES = ["tts","stt","llm","classify","embed","rerank","telephony","whatsapp","email","number_rental"];
const UNITS = ["per_1k_chars","per_min","per_1M_in","per_1M_out","per_1M_cache_read",
               "per_1M_cache_write","per_search","per_1M_tokens","per_message","per_month"];
const DIRECTIONS   = ["inbound", "outbound"];
const NUMBER_TYPES = ["landline", "mobile", "tollfree", "premium"];
const CALLER_TYPES = ["", "landline", "mobile"];
const WA_CATEGORIES = ["marketing", "utility", "authentication", "authentication-international", "service"];
// Capabilities whose route is composed from parts rather than typed.
const STRUCTURED = new Set(["telephony", "whatsapp", "number_rental"]);
// Sensible default unit per capability; still editable.
const DEFAULT_UNIT = { tts: "per_1k_chars", stt: "per_min", llm: "per_1M_in", classify: "per_1M_in", embed: "per_1M_tokens", rerank: "per_search",
                       telephony: "per_min", whatsapp: "per_message", email: "per_message", number_rental: "per_month" };

const SOURCE_TONE = {
  manual:     { bg: "rgba(122,63,228,0.12)", fg: "#6D28D9" },
  telnyx_api: { bg: "rgba(59,125,240,0.12)", fg: "#2563EB" },
  meta_card:  { bg: "rgba(34,176,125,0.12)", fg: "#0F7A5A" },
};

const EMPTY_PARTS = { direction: "inbound", country: "", type: "landline", caller: "", category: "utility", anyCountry: false };
const EMPTY_FORM  = { provider: "", capability: "telephony", modelOrRoute: "", unit: "per_min", costUsd: "", effectiveFrom: "", notes: "" };

function fmtCost(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n === 0 ? "0" : n.toFixed(n < 0.01 ? 6 : 4);
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return String(d); }
}
function SourceChip({ source, t }) {
  const c = SOURCE_TONE[source] || SOURCE_TONE.manual;
  return <Chip size="small" label={t(`rcSource_${source}`, source)} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: c.bg, color: c.fg }} />;
}

// Compose the route key from picker parts; mirrors the server's key shapes.
function composeRoute(capability, p) {
  if (capability === "telephony") {
    if (!p.country) return "";
    const base = `${p.direction}/${p.country}/${p.type}`;
    return p.direction === "inbound" && p.caller ? `${base}/from-${p.caller}` : base;
  }
  if (capability === "whatsapp")      return `${p.category}/${p.anyCountry ? "*" : (p.country || "")}`.replace(/\/$/, "");
  if (capability === "number_rental") return p.country ? `${p.country}/${p.type}` : "";
  return "";
}
// Reverse: pre-fill the picker from an existing key when adding a new version.
function parseRoute(capability, key) {
  const p = { ...EMPTY_PARTS };
  const s = String(key || "");
  if (capability === "telephony") {
    const m = s.match(/^(inbound|outbound)\/([A-Z]{2})\/([a-z]+)(?:\/from-([a-z]+))?$/);
    if (m) { p.direction = m[1]; p.country = m[2]; p.type = m[3]; p.caller = m[4] || ""; }
  } else if (capability === "whatsapp") {
    const m = s.match(/^([a-z-]+)\/(\*|[A-Z]{2}|region:.+)$/);
    if (m) { p.category = m[1]; if (m[2] === "*") p.anyCountry = true; else if (/^[A-Z]{2}$/.test(m[2])) p.country = m[2]; }
  } else if (capability === "number_rental") {
    const m = s.match(/^([A-Z]{2})\/([a-z]+)$/);
    if (m) { p.country = m[1]; p.type = m[2]; }
  }
  return p;
}

function RateCardsAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [rowsPerPage]         = useState(25);
  const [search, setSearch]   = useState("");
  const [provider, setProvider]     = useState("");
  const [capability, setCapability] = useState("");
  const [facets, setFacets]   = useState({ providers: [], capabilities: [] });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [toast, setToast]     = useState(null);

  // sync
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing]       = useState(false);
  const pollRef = useRef(null);

  // dialogs
  const [addOpen, setAddOpen]       = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [parts, setParts]           = useState(EMPTY_PARTS);
  const [saving, setSaving]         = useState(false);
  const [dlgError, setDlgError]     = useState("");
  const [histOpen, setHistOpen]     = useState(null);
  const [hist, setHist]             = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFormat, setImportFormat] = useState("generic");
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview]       = useState(null);
  const [importing, setImporting]   = useState(false);
  const fileRef = useRef(null);

  const showToast = (msg, sev = "success", ms = 6000) => { setToast({ msg, sev }); setTimeout(() => setToast(null), ms); };

  const load = useCallback(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams({
      page: page + 1, size: rowsPerPage,
      ...(search ? { q: search } : {}),
      ...(provider ? { provider } : {}),
      ...(capability ? { capability } : {}),
    });
    axiosInstance.get(`/ai/admin/rate-cards?${params}`)
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(e => setError(e.response?.data?.error || t("rcLoadFailed")))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search, provider, capability, t]);

  const loadFacets = useCallback(() => {
    axiosInstance.get("/ai/admin/rate-cards/facets").then(r => setFacets(r.data)).catch(() => {});
  }, []);

  const loadStatus = useCallback(() => axiosInstance.get("/ai/admin/rate-cards/sync/status").then(r => { setSyncStatus(r.data); return r.data; }).catch(() => null), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFacets(); loadStatus().then(s => { if (s?.running) startPolling(); }); }, [loadFacets, loadStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Countries are only needed inside the add dialog; fetched once.
  const ensureCountries = () => {
    if (countries.length) return;
    axiosInstance.get("/ai/admin/rate-cards/countries").then(r => setCountries(r.data.countries || [])).catch(() => {});
  };

  const summarizeSync = (last) => {
    if (!last) return;
    const tot = last.totals || {};
    const per = Object.entries(last.products || {}).map(([slug, p]) => `${slug}: ${p.inserted ?? 0} new / ${p.rows ?? 0} routes`).join(" · ");
    if (last.errors?.length) {
      showToast(`${t("rcSyncErrors", { n: last.errors.length })} — ${last.errors.map(e => `${e.slug}: ${e.error}`).join(" | ")}`, "warning", 15000);
    } else {
      showToast(`${t("rcSyncDone", { inserted: tot.inserted ?? 0, unchanged: tot.unchanged ?? 0, manual: tot.skippedManual ?? 0 })} ${per ? `(${per})` : ""}`, "success", 10000);
    }
  };

  const startPolling = () => {
    setSyncing(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await loadStatus();
      if (s && !s.running) {
        clearInterval(pollRef.current); pollRef.current = null;
        setSyncing(false);
        summarizeSync(s.last);
        load(); loadFacets();
      }
    }, 3000);
  };

  const doSync = async () => {
    try {
      await axiosInstance.post("/ai/admin/rate-cards/sync/telnyx");
      showToast(t("rcSyncStarted"), "info", 8000);
      startPolling();
    } catch (e) {
      if (e.response?.status === 409) { startPolling(); return; }
      showToast(e.response?.data?.error || t("rcSyncFailed"), "error");
    }
  };

  const openAdd = (seed) => {
    ensureCountries();
    if (seed) {
      setForm({ ...EMPTY_FORM, provider: seed.provider, capability: seed.capability, modelOrRoute: seed.modelOrRoute, unit: seed.unit, costUsd: String(seed.costUsd ?? "") });
      setParts(parseRoute(seed.capability, seed.modelOrRoute));
    } else {
      setForm(EMPTY_FORM); setParts(EMPTY_PARTS);
    }
    setDlgError(""); setAddOpen(true);
  };

  // Keep modelOrRoute in step with the picker for structured capabilities.
  const structured = STRUCTURED.has(form.capability);
  const composed   = useMemo(() => composeRoute(form.capability, parts), [form.capability, parts]);
  useEffect(() => { if (structured) setForm(f => ({ ...f, modelOrRoute: composed })); }, [structured, composed]);

  const setCapabilityInForm = (cap) => {
    setForm(f => ({ ...f, capability: cap, unit: DEFAULT_UNIT[cap] || f.unit, modelOrRoute: STRUCTURED.has(cap) ? composeRoute(cap, parts) : "" }));
  };

  const doAdd = async () => {
    setSaving(true); setDlgError("");
    try {
      await axiosInstance.post("/ai/admin/rate-cards", { ...form, costUsd: Number(form.costUsd), effectiveFrom: form.effectiveFrom || undefined });
      setAddOpen(false); setForm(EMPTY_FORM); setParts(EMPTY_PARTS); load(); loadFacets();
      showToast(t("rcAdded"));
    } catch (e) {
      setDlgError(e.response?.data?.error || t("rcAddFailed"));
    } finally { setSaving(false); }
  };

  const openHistory = async (row) => {
    setHistOpen(row); setHist([]); setHistLoading(true);
    try {
      const params = new URLSearchParams({ provider: row.provider, capability: row.capability, modelOrRoute: row.modelOrRoute, unit: row.unit });
      const { data } = await axiosInstance.get(`/ai/admin/rate-cards/history?${params}`);
      setHist(data.rows || []);
    } catch { setHist([]); }
    finally { setHistLoading(false); }
  };

  const doPreview = async () => {
    if (!importFile) return;
    setImporting(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await axiosInstance.post(`/ai/admin/rate-cards/import?format=${importFormat}&dryRun=1`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { setPreview({ error: e.response?.data?.error || t("rcImportFailed") }); }
    finally { setImporting(false); }
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await axiosInstance.post(`/ai/admin/rate-cards/import?format=${importFormat}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      showToast(t("rcImportDone", { inserted: data.inserted ?? 0, unchanged: data.unchanged ?? 0, invalid: data.invalid ?? 0 }));
      setImportOpen(false); setImportFile(null); setPreview(null); load(); loadFacets();
    } catch (e) { setPreview({ error: e.response?.data?.error || t("rcImportFailed") }); }
    finally { setImporting(false); }
  };

  const inputSx  = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const headSx   = { fontSize: 12, fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" };
  const cellSx   = { fontSize: 13 };
  const countryLabel = (iso) => { const c = countries.find(x => x.iso === iso); return c ? `${c.name} (${c.iso})` : iso; };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: alpha("#7A3FE4", 0.12), display: "grid", placeItems: "center" }}>
          <Receipt size={20} color="#7A3FE4" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{t("rcTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("rcSubtitle")}</Typography>
        </Box>
        <Tooltip title={syncStatus?.last?.at ? t("rcLastSync", { at: fmtDate(syncStatus.last.at) }) : t("rcNeverSynced")}>
          <span>
            <Button
              variant="outlined" size="small" onClick={doSync} disabled={syncing}
              startIcon={syncing ? <CircularProgress size={13} /> : <CloudDownload size={14} />}
              sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}
            >
              {syncing ? t("rcSyncing") : t("rcSyncTelnyx")}
            </Button>
          </span>
        </Tooltip>
        <Button variant="outlined" size="small" onClick={() => { setImportOpen(true); setPreview(null); setImportFile(null); }}
          startIcon={<Upload size={14} />} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
          {t("rcImportCsv")}
        </Button>
        <Button variant="contained" size="small" onClick={() => openAdd(null)}
          startIcon={<Plus size={14} />} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}>
          {t("rcAddRate")}
        </Button>
      </Box>

      {syncing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
      {toast && <Alert severity={toast.sev} sx={{ mb: 2, borderRadius: "10px", fontSize: 12.5 }}>{toast.msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <TextField size="small" placeholder={t("rcSearch")} value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("rcProvider")}</InputLabel>
          <Select label={t("rcProvider")} value={provider} onChange={e => { setProvider(e.target.value); setPage(0); }} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">{t("rcAll")}</MenuItem>
            {facets.providers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("rcCapability")}</InputLabel>
          <Select label={t("rcCapability")} value={capability} onChange={e => { setCapability(e.target.value); setPage(0); }} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">{t("rcAll")}</MenuItem>
            {CAPABILITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title={t("refresh")}>
          <IconButton size="small" onClick={() => { load(); loadFacets(); loadStatus(); }}><RefreshCw size={15} /></IconButton>
        </Tooltip>
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>{t("rcProvider")}</TableCell>
              <TableCell sx={headSx}>{t("rcCapability")}</TableCell>
              <TableCell sx={headSx}>{t("rcRoute")}</TableCell>
              <TableCell sx={headSx}>{t("rcUnit")}</TableCell>
              <TableCell sx={headSx} align="right">{t("rcCost")}</TableCell>
              <TableCell sx={headSx}>{t("rcEffective")}</TableCell>
              <TableCell sx={headSx}>{t("rcSource")}</TableCell>
              <TableCell sx={headSx} align="center">{t("rcVersions")}</TableCell>
              <TableCell sx={headSx} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary", fontSize: 13 }}>{t("rcEmpty")}</TableCell></TableRow>
            )}
            {!loading && rows.map(r => (
              <TableRow key={`${r.provider}|${r.capability}|${r.modelOrRoute}|${r.unit}`} hover sx={r.isFuture ? { opacity: 0.8 } : undefined}>
                <TableCell sx={cellSx}>{r.provider}</TableCell>
                <TableCell sx={cellSx}>{r.capability}</TableCell>
                <TableCell sx={{ ...cellSx, fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.modelOrRoute}</TableCell>
                <TableCell sx={{ ...cellSx, color: "text.secondary", fontSize: 12 }}>{r.unit}</TableCell>
                <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", fontWeight: 600 }} align="right">
                  {r.currency && r.currency !== "USD" ? `${r.currency} ` : "$"}{fmtCost(r.costUsd)}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontSize: 12, whiteSpace: "nowrap" }}>
                  {fmtDate(r.effectiveFrom)}
                  {r.isFuture ? (
                    <Chip size="small" icon={<Clock size={10} />} label={t("rcScheduled")}
                      sx={{ ml: 0.75, height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha("#E8930C", 0.12), color: "#B45309", "& .MuiChip-icon": { color: "inherit" } }} />
                  ) : r.futureCount > 0 ? (
                    <Tooltip title={t("rcFutureScheduled", { n: r.futureCount })}>
                      <Chip size="small" icon={<Clock size={10} />} label={`+${r.futureCount}`}
                        sx={{ ml: 0.75, height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha("#E8930C", 0.12), color: "#B45309", "& .MuiChip-icon": { color: "inherit" } }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell><SourceChip source={r.source} t={t} /></TableCell>
                <TableCell sx={cellSx} align="center">{r.versionCount}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={t("rcHistory")}><IconButton size="small" onClick={() => openHistory(r)}><History size={14} /></IconButton></Tooltip>
                  <Tooltip title={t("rcNewEffective")}><IconButton size="small" onClick={() => openAdd(r)}><Plus size={14} /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[rowsPerPage]} />
      </TableContainer>

      {/* Add / new effective rate */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{t("rcAddRateTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>{t("rcAddRateDesc")}</Typography>
          {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField size="small" label={t("rcProvider")} value={form.provider}
              onChange={e => setForm(f => ({ ...f, provider: e.target.value.toLowerCase() }))}
              placeholder="telnyx · zain_jo · elevenlabs" sx={inputSx} />
            <FormControl size="small"><InputLabel>{t("rcCapability")}</InputLabel>
              <Select label={t("rcCapability")} value={form.capability} onChange={e => setCapabilityInForm(e.target.value)}>
                {CAPABILITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Route picker — composed for structured capabilities */}
            {form.capability === "telephony" && (<>
              <FormControl size="small"><InputLabel>{t("rcDirection")}</InputLabel>
                <Select label={t("rcDirection")} value={parts.direction} onChange={e => setParts(p => ({ ...p, direction: e.target.value, caller: e.target.value === "outbound" ? "" : p.caller }))}>
                  {DIRECTIONS.map(d => <MenuItem key={d} value={d}>{t(`rcDir_${d}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small"><InputLabel>{t("rcCountry")}</InputLabel>
                <Select label={t("rcCountry")} value={parts.country} onChange={e => setParts(p => ({ ...p, country: e.target.value }))} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  {countries.map(c => <MenuItem key={c.iso} value={c.iso}>{c.name} ({c.iso})</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small"><InputLabel>{t("rcNumberType")}</InputLabel>
                <Select label={t("rcNumberType")} value={parts.type} onChange={e => setParts(p => ({ ...p, type: e.target.value }))}>
                  {NUMBER_TYPES.map(x => <MenuItem key={x} value={x}>{t(`rcType_${x}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" disabled={parts.direction !== "inbound"}><InputLabel>{t("rcCallerType")}</InputLabel>
                <Select label={t("rcCallerType")} value={parts.caller} onChange={e => setParts(p => ({ ...p, caller: e.target.value }))}>
                  {CALLER_TYPES.map(x => <MenuItem key={x || "any"} value={x}>{x ? t(`rcType_${x}`) : t("rcCallerAny")}</MenuItem>)}
                </Select>
              </FormControl>
            </>)}
            {form.capability === "whatsapp" && (<>
              <FormControl size="small"><InputLabel>{t("rcCategory")}</InputLabel>
                <Select label={t("rcCategory")} value={parts.category} onChange={e => setParts(p => ({ ...p, category: e.target.value }))}>
                  {WA_CATEGORIES.map(c => <MenuItem key={c} value={c}>{t(`rcCat_${c}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small"><InputLabel>{t("rcCountry")}</InputLabel>
                <Select label={t("rcCountry")} value={parts.anyCountry ? "*" : parts.country}
                  onChange={e => setParts(p => e.target.value === "*" ? ({ ...p, anyCountry: true, country: "" }) : ({ ...p, anyCountry: false, country: e.target.value }))}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  <MenuItem value="*">{t("rcAnyCountry")}</MenuItem>
                  {countries.map(c => <MenuItem key={c.iso} value={c.iso}>{c.name} ({c.iso})</MenuItem>)}
                </Select>
              </FormControl>
            </>)}
            {form.capability === "number_rental" && (<>
              <FormControl size="small"><InputLabel>{t("rcCountry")}</InputLabel>
                <Select label={t("rcCountry")} value={parts.country} onChange={e => setParts(p => ({ ...p, country: e.target.value }))} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  {countries.map(c => <MenuItem key={c.iso} value={c.iso}>{c.name} ({c.iso})</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small"><InputLabel>{t("rcNumberType")}</InputLabel>
                <Select label={t("rcNumberType")} value={parts.type} onChange={e => setParts(p => ({ ...p, type: e.target.value }))}>
                  {NUMBER_TYPES.map(x => <MenuItem key={x} value={x}>{t(`rcType_${x}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </>)}
            {structured ? (
              <Box sx={{ gridColumn: { sm: "1 / -1" }, display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, borderRadius: "10px", bgcolor: alpha("#7A3FE4", 0.06) }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("rcRoutePreview")}</Typography>
                <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600 }}>{form.modelOrRoute || "—"}</Typography>
              </Box>
            ) : (
              <TextField size="small" label={t("rcModelId")} value={form.modelOrRoute}
                onChange={e => setForm(f => ({ ...f, modelOrRoute: e.target.value }))}
                placeholder="eleven_v3 · claude-haiku-4-5 · nova-3-streaming" helperText={t("rcModelIdHelp")}
                sx={{ ...inputSx, gridColumn: { sm: "1 / -1" } }} />
            )}

            <FormControl size="small"><InputLabel>{t("rcUnit")}</InputLabel>
              <Select label={t("rcUnit")} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                {UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label={t("rcCostUsd")} type="number" inputProps={{ step: "0.000001", min: 0 }} value={form.costUsd}
              onChange={e => setForm(f => ({ ...f, costUsd: e.target.value }))} sx={inputSx} />
            <TextField size="small" label={t("rcEffectiveFrom")} type="datetime-local" InputLabelProps={{ shrink: true }} value={form.effectiveFrom}
              onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))} helperText={t("rcEffectiveHelp")} sx={inputSx} />
            <TextField size="small" label={t("rcNotes")} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} sx={inputSx} inputProps={{ maxLength: 255 }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={doAdd} disabled={saving || !form.provider || !form.modelOrRoute || form.costUsd === ""}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {saving ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : t("rcSaveRate")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History */}
      <Dialog open={Boolean(histOpen)} onClose={() => setHistOpen(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 1 }}>
          <History size={16} /> {t("rcHistoryTitle")}
          <IconButton size="small" onClick={() => setHistOpen(null)} sx={{ ml: "auto" }}><X size={16} /></IconButton>
        </DialogTitle>
        <DialogContent>
          {histOpen && (
            <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "text.secondary", mb: 1.5 }}>
              {histOpen.provider} · {histOpen.capability} · {histOpen.modelOrRoute} · {histOpen.unit}
            </Typography>
          )}
          {histLoading ? <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={20} /></Box> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={headSx}>{t("rcEffective")}</TableCell>
                <TableCell sx={headSx} align="right">{t("rcCost")}</TableCell>
                <TableCell sx={headSx}>{t("rcSource")}</TableCell>
                <TableCell sx={headSx}>{t("rcNotes")}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {hist.map((h, i) => (
                  <TableRow key={h.id} sx={{ opacity: h.isCurrent ? 1 : 0.75 }}>
                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {fmtDate(h.effectiveFrom)}
                      {h.isCurrent ? <Chip size="small" label={t("rcCurrent")} sx={{ ml: 0.75, height: 16, fontSize: 10 }} /> : null}
                      {!h.isCurrent && i === 0 ? <Chip size="small" label={t("rcScheduled")} sx={{ ml: 0.75, height: 16, fontSize: 10, bgcolor: alpha("#E8930C", 0.12), color: "#B45309" }} /> : null}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>${fmtCost(h.costUsd)}</TableCell>
                    <TableCell><SourceChip source={h.source} t={t} /></TableCell>
                    <TableCell sx={{ fontSize: 11.5, color: "text.secondary" }}>{h.notes || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Import */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{t("rcImportTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>{t("rcImportDesc")}</Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("rcImportFormat")}</InputLabel>
              <Select label={t("rcImportFormat")} value={importFormat} onChange={e => { setImportFormat(e.target.value); setPreview(null); }}>
                <MenuItem value="generic">{t("rcFormatGeneric")}</MenuItem>
                <MenuItem value="meta">{t("rcFormatMeta")}</MenuItem>
              </Select>
            </FormControl>
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={e => { setImportFile(e.target.files?.[0] || null); setPreview(null); }} />
            <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()} startIcon={<Upload size={14} />} sx={{ textTransform: "none", borderRadius: "10px" }}>
              {importFile ? importFile.name : t("rcChooseFile")}
            </Button>
            <Button variant="outlined" size="small" onClick={doPreview} disabled={!importFile || importing} startIcon={<FileCheck2 size={14} />} sx={{ textTransform: "none", borderRadius: "10px" }}>
              {t("rcPreview")}
            </Button>
          </Box>
          {importFormat === "generic" && (
            <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "text.secondary", mb: 2 }}>
              provider,capability,modelOrRoute,unit,costUsd,effectiveFrom,currency,notes
            </Typography>
          )}
          {importFormat === "meta" && <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>{t("rcMetaHelp")}</Typography>}
          {preview?.error && <Alert severity="error" sx={{ mb: 2 }}>{preview.error}</Alert>}
          {preview && !preview.error && (
            <Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                {t("rcPreviewSummary", { rows: preview.rows?.length ?? 0, errors: preview.errors?.length ?? 0, unmapped: preview.unmapped?.length ?? 0 })}
              </Typography>
              {preview.errors?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
                  {preview.errors.slice(0, 8).map(e => <div key={e.line}>L{e.line}: {e.error}</div>)}
                  {preview.errors.length > 8 && <div>…{preview.errors.length - 8} more</div>}
                </Alert>
              )}
              {preview.unmapped?.length > 0 && (
                <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>
                  {t("rcUnmappedMarkets")}: {preview.unmapped.slice(0, 12).map(u => u.market).join(", ")}{preview.unmapped.length > 12 ? " …" : ""}
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 260, border: "1px solid", borderColor: "divider", borderRadius: "10px" }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell sx={headSx}>{t("rcProvider")}</TableCell><TableCell sx={headSx}>{t("rcCapability")}</TableCell>
                    <TableCell sx={headSx}>{t("rcRoute")}</TableCell><TableCell sx={headSx}>{t("rcUnit")}</TableCell>
                    <TableCell sx={headSx} align="right">{t("rcCost")}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {(preview.rows || []).slice(0, 50).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: 12 }}>{r.provider}</TableCell><TableCell sx={{ fontSize: 12 }}>{r.capability}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{r.modelOrRoute}</TableCell><TableCell sx={{ fontSize: 12 }}>{r.unit}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }} align="right">{fmtCost(r.costUsd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setImportOpen(false)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={doImport} disabled={!preview || preview.error || !(preview.rows?.length) || importing}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {importing ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : t("rcCommitImport", { n: preview?.rows?.length ?? 0 })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RateCardsAdmin;
